/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BlockLibraryPanel from './builder/BlockLibraryPanel.jsx';
import BuilderToolbar from './builder/BuilderToolbar.jsx';
import VoucherCanvas from './builder/VoucherCanvas.jsx';
import InspectorPanel from './builder/InspectorPanel.jsx';
import {
  BLOCK_LIBRARY,
  DEFAULT_LAYOUT,
  PAGE_PRESETS,
  createBlock,
  normalizeBuilderState,
  snapValue,
} from './builder/builderModel.js';
import {
  alignBlockBottom,
  alignBlockCenterX,
  alignBlockCenterY,
  alignBlockLeft,
  alignBlockRight,
  alignBlockTop,
  applyPresetPosition,
  clampRect,
  distributeHorizontally,
  distributeVertically,
  snapRect,
} from './builder/layoutTools.js';

function rotateSize(width, height, orientation) {
  return orientation === 'landscape' ? { width: height, height: width } : { width, height };
}

export default function VoucherTemplateVisualBuilder({ value, onChange }) {
  const [state, setState] = useState(() => normalizeBuilderState(value));
  const [primarySelectedBlockId, setPrimarySelectedBlockId] = useState(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState([]);
  const [saveState, setSaveState] = useState('saved');
  const lastAppliedRef = useRef(JSON.stringify(normalizeBuilderState(value)));
  const lastEmittedRef = useRef(lastAppliedRef.current);

  const selectedBlock = useMemo(
    () => state.blocks.find((block) => block.id === primarySelectedBlockId) || null,
    [state.blocks, primarySelectedBlockId],
  );

  // Keyboard shortcuts intentionally bind to current selected block and page snap settings.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const incoming = normalizeBuilderState(value);
    const key = JSON.stringify(incoming);
    if (key === lastAppliedRef.current) return;
    setState(incoming);
    lastAppliedRef.current = key;
    setSaveState('saved');
  }, [value]);

  useEffect(() => {
    const key = JSON.stringify(state);
    if (key === lastEmittedRef.current) return undefined;
    setSaveState('saving');
    const timer = window.setTimeout(() => {
      onChange?.(state);
      lastEmittedRef.current = key;
      lastAppliedRef.current = key;
      setSaveState('saved');
    }, 280);
    return () => window.clearTimeout(timer);
  }, [state, onChange]);

  const updateState = (updater) => {
    const current = state;
    const next = typeof updater === 'function' ? updater(current) : updater;
    setState(next);
    setSaveState('pending');
  };

  const selectBlock = (blockId, options = {}) => {
    const id = String(blockId || '').trim();
    if (!id) {
      setPrimarySelectedBlockId(null);
      setSelectedBlockIds([]);
      return;
    }
    const addToSelection = Boolean(options.addToSelection);
    if (addToSelection) {
      setSelectedBlockIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
      setPrimarySelectedBlockId(id);
      return;
    }
    setPrimarySelectedBlockId(id);
    setSelectedBlockIds([id]);
  };

  const onPageChange = (patch) => {
    updateState((current) => {
      let nextPage = { ...current.page, ...patch };
      if (patch.page_size && patch.page_size !== current.page.page_size) {
        const preset = PAGE_PRESETS[patch.page_size] || PAGE_PRESETS.A4;
        const sized = rotateSize(preset.width, preset.height, nextPage.orientation);
        nextPage = { ...nextPage, width: sized.width, height: sized.height };
      }
      if (patch.orientation && patch.orientation !== current.page.orientation) {
        const preset = PAGE_PRESETS[nextPage.page_size] || PAGE_PRESETS.A4;
        const sized = rotateSize(preset.width, preset.height, patch.orientation);
        nextPage = { ...nextPage, width: sized.width, height: sized.height };
      }
      return { ...current, page: nextPage };
    });
  };

  const onAddBlock = (indexLike) => {
    const item = BLOCK_LIBRARY[Number(indexLike)];
    if (!item) return;
    let createdId = '';
    updateState((current) => {
      const block = createBlock(item, current, 24, 24);
      createdId = block.id;
      return { ...current, blocks: [...current.blocks, block] };
    });
    if (createdId) selectBlock(createdId);
  };

  const onCreateBlockAt = (blockRef, x, y) => {
    const item = Number.isInteger(Number(blockRef))
      ? BLOCK_LIBRARY[Number(blockRef)]
      : BLOCK_LIBRARY.find((entry) => entry.type === blockRef);
    if (!item) return;
    let createdId = '';
    updateState((current) => {
      const block = createBlock(item, current, x, y);
      const snapped = current.page.snap_enabled
        ? snapRect({ ...block, x, y }, current.page.grid_size)
        : { ...block, x, y };
      const bounded = clampRect(snapped, current.page);
      block.x = bounded.x;
      block.y = bounded.y;
      createdId = block.id;
      return { ...current, blocks: [...current.blocks, block] };
    });
    if (createdId) selectBlock(createdId);
  };

  const onUpdateBlock = (blockId, patch) => {
    updateState((current) => {
      const nextBlocks = current.blocks.map((block) => {
        if (block.id !== blockId) return block;
        const merged = { ...block, ...patch, style: patch.style ? { ...block.style, ...patch.style } : block.style };
        const maybeSnapped = current.page.snap_enabled
          ? snapRect(merged, current.page.grid_size)
          : merged;
        return clampRect(maybeSnapped, current.page);
      });
      return { ...current, blocks: nextBlocks };
    });
  };

  const onUpdateSelected = (patch) => {
    if (!selectedBlock) return;
    onUpdateBlock(selectedBlock.id, patch);
  };

  const onDuplicateSelected = () => {
    if (!selectedBlock) return;
    let createdId = '';
    updateState((current) => {
      const clone = {
        ...selectedBlock,
        id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        ...clampRect({
          ...selectedBlock,
          x: Number(selectedBlock.x || 0) + 16,
          y: Number(selectedBlock.y || 0) + 16,
        }, current.page),
        z_index: (current.blocks.reduce((m, b) => Math.max(m, Number(b.z_index || 0)), 0) || 0) + 1,
      };
      createdId = clone.id;
      return { ...current, blocks: [...current.blocks, clone] };
    });
    if (createdId) selectBlock(createdId);
  };

  const onDeleteSelected = () => {
    if (!selectedBlock) return;
    const selectedSet = new Set(selectedBlockIds);
    updateState((current) => ({ ...current, blocks: current.blocks.filter((block) => !selectedSet.has(block.id)) }));
    setPrimarySelectedBlockId(null);
    setSelectedBlockIds([]);
  };

  const onBringForward = () => {
    if (!selectedBlock) return;
    updateState((current) => ({
      ...current,
      blocks: current.blocks.map((block) => (
        block.id === selectedBlock.id ? { ...block, z_index: Number(block.z_index || 1) + 1 } : block
      )),
    }));
  };

  const onSendBackward = () => {
    if (!selectedBlock) return;
    updateState((current) => ({
      ...current,
      blocks: current.blocks.map((block) => (
        block.id === selectedBlock.id
          ? { ...block, z_index: Math.max(1, Number(block.z_index || 1) - 1) }
          : block
      )),
    }));
  };

  const onToggleLockSelected = () => {
    if (!selectedBlock) return;
    onUpdateBlock(selectedBlock.id, { locked: !Boolean(selectedBlock.locked) });
  };

  const onAlign = (mode) => {
    if (!selectedBlock) return;
    const canvas = { width: state.page.width, height: state.page.height };
    let next = { ...selectedBlock };
    if (mode === 'left') next = alignBlockLeft(next, canvas);
    else if (mode === 'center_x') next = alignBlockCenterX(next, canvas);
    else if (mode === 'right') next = alignBlockRight(next, canvas);
    else if (mode === 'top') next = alignBlockTop(next, canvas);
    else if (mode === 'center_y') next = alignBlockCenterY(next, canvas);
    else if (mode === 'bottom') next = alignBlockBottom(next, canvas);
    const finalRect = clampRect(state.page.snap_enabled ? snapRect(next, state.page.grid_size) : next, canvas);
    onUpdateBlock(selectedBlock.id, { x: finalRect.x, y: finalRect.y });
  };

  const onApplyPreset = (presetName) => {
    if (!selectedBlock) return;
    const canvas = { width: state.page.width, height: state.page.height };
    const preset = applyPresetPosition(selectedBlock, presetName, canvas, state.page.grid_size);
    onUpdateBlock(selectedBlock.id, { x: preset.x, y: preset.y });
  };

  const onDistributeHorizontal = () => {
    if (selectedBlockIds.length < 3) return;
    const selectedSet = new Set(selectedBlockIds);
    const selectedBlocks = state.blocks.filter((block) => selectedSet.has(block.id));
    const distributed = distributeHorizontally(selectedBlocks, state.page, state.page.grid_size);
    const map = new Map(distributed.map((block) => [block.id, block]));
    updateState((current) => ({
      ...current,
      blocks: current.blocks.map((block) => map.get(block.id) || block),
    }));
  };

  const onDistributeVertical = () => {
    if (selectedBlockIds.length < 3) return;
    const selectedSet = new Set(selectedBlockIds);
    const selectedBlocks = state.blocks.filter((block) => selectedSet.has(block.id));
    const distributed = distributeVertically(selectedBlocks, state.page, state.page.grid_size);
    const map = new Map(distributed.map((block) => [block.id, block]));
    updateState((current) => ({
      ...current,
      blocks: current.blocks.map((block) => map.get(block.id) || block),
    }));
  };

  const moveSelectedBy = (dx, dy) => {
    if (!selectedBlock || selectedBlock.locked) return;
    const unit = Number(state.page.snap_enabled ? state.page.grid_size : 1) || 1;
    const next = {
      x: Number(selectedBlock.x || 0) + (dx * unit),
      y: Number(selectedBlock.y || 0) + (dy * unit),
    };
    onUpdateBlock(selectedBlock.id, next);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (event) => {
      const tag = String(event.target?.tagName || '').toLowerCase();
      const isTypingTarget = ['input', 'textarea', 'select'].includes(tag) || event.target?.isContentEditable;
      if (isTypingTarget || !selectedBlock) return;

      const key = String(event.key || '').toLowerCase();
      const withCmd = event.metaKey || event.ctrlKey;

      if (key === 'delete' || key === 'backspace') {
        event.preventDefault();
        onDeleteSelected();
        return;
      }
      if (withCmd && key === 'd') {
        event.preventDefault();
        onDuplicateSelected();
        return;
      }
      if (withCmd && key === ']') {
        event.preventDefault();
        onBringForward();
        return;
      }
      if (withCmd && key === '[') {
        event.preventDefault();
        onSendBackward();
        return;
      }

      const multiplier = event.shiftKey ? 10 : 1;
      if (key === 'arrowleft') {
        event.preventDefault();
        moveSelectedBy(-multiplier, 0);
      } else if (key === 'arrowright') {
        event.preventDefault();
        moveSelectedBy(multiplier, 0);
      } else if (key === 'arrowup') {
        event.preventDefault();
        moveSelectedBy(0, -multiplier);
      } else if (key === 'arrowdown') {
        event.preventDefault();
        moveSelectedBy(0, multiplier);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedBlock, state.page.grid_size, state.page.snap_enabled]);

  const onClearCanvas = () => {
    updateState((current) => ({ ...current, blocks: [] }));
    setPrimarySelectedBlockId(null);
    setSelectedBlockIds([]);
  };

  const onResetLayout = () => {
    setState(DEFAULT_LAYOUT);
    setSaveState('pending');
    setPrimarySelectedBlockId(null);
    setSelectedBlockIds([]);
  };

  useEffect(() => {
    if (!primarySelectedBlockId) return;
    const exists = state.blocks.some((block) => block.id === primarySelectedBlockId);
    if (!exists) {
      setPrimarySelectedBlockId(null);
      setSelectedBlockIds([]);
      return;
    }
    setSelectedBlockIds((prev) => prev.filter((id) => state.blocks.some((block) => block.id === id)));
  }, [state.blocks, primarySelectedBlockId]);

  return (
    <div className="space-y-3">
      <BuilderToolbar
        page={state.page}
        onPageChange={onPageChange}
        onClearCanvas={onClearCanvas}
        onResetLayout={onResetLayout}
        onAlign={onAlign}
        onApplyPreset={onApplyPreset}
        onDuplicateSelected={onDuplicateSelected}
        onDeleteSelected={onDeleteSelected}
        onToggleLockSelected={onToggleLockSelected}
        onBringForward={onBringForward}
        onSendBackward={onSendBackward}
        onDistributeHorizontal={onDistributeHorizontal}
        onDistributeVertical={onDistributeVertical}
        hasSelected={Boolean(selectedBlock)}
        hasMultipleSelected={selectedBlockIds.length > 1}
        selectedLocked={Boolean(selectedBlock?.locked)}
        saveState={saveState}
        selectedBlockLabel={selectedBlock?.type || ''}
      />

      <div className="overflow-hidden rounded-2xl border border-cyan-300/20 bg-[#030b19] p-3">
        <div className="hidden h-[760px] min-h-0 gap-4 lg:grid lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[220px_minmax(0,1fr)_320px]">
          <BlockLibraryPanel onAddBlock={onAddBlock} />

          <VoucherCanvas
            state={state}
            selectedId={primarySelectedBlockId || ''}
            selectedIds={selectedBlockIds}
            onSelect={selectBlock}
            onUpdateBlock={onUpdateBlock}
            onCreateBlockAt={onCreateBlockAt}
          />

          <div className="hidden h-full min-h-0 xl:block">
            <InspectorPanel
              block={selectedBlock}
              onChange={onUpdateSelected}
              onDelete={onDeleteSelected}
              onDuplicate={onDuplicateSelected}
              onBringForward={onBringForward}
              onSendBackward={onSendBackward}
            />
          </div>
        </div>
        <div className="mt-3 hidden h-[320px] min-h-0 lg:block xl:hidden">
          <InspectorPanel
            block={selectedBlock}
            onChange={onUpdateSelected}
            onDelete={onDeleteSelected}
            onDuplicate={onDuplicateSelected}
            onBringForward={onBringForward}
            onSendBackward={onSendBackward}
          />
        </div>

        <div className="space-y-3 lg:hidden">
          <VoucherCanvas
            state={state}
            selectedId={primarySelectedBlockId || ''}
            selectedIds={selectedBlockIds}
            onSelect={selectBlock}
            onUpdateBlock={onUpdateBlock}
            onCreateBlockAt={onCreateBlockAt}
          />
          <details className="rounded-xl border border-cyan-300/20 bg-[#061020]/70 p-2">
            <summary className="cursor-pointer text-sm font-semibold text-cyan-200">Blocks Library</summary>
            <div className="mt-2">
              <BlockLibraryPanel onAddBlock={onAddBlock} />
            </div>
          </details>
          <details className="rounded-xl border border-cyan-300/20 bg-[#061020]/70 p-2">
            <summary className="cursor-pointer text-sm font-semibold text-cyan-200">Inspector</summary>
            <div className="mt-2 h-[360px] min-h-0">
              <InspectorPanel
                block={selectedBlock}
                onChange={onUpdateSelected}
                onDelete={onDeleteSelected}
                onDuplicate={onDuplicateSelected}
                onBringForward={onBringForward}
                onSendBackward={onSendBackward}
              />
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

export { DEFAULT_LAYOUT };
